import React, { useState } from 'react';
import { 
  Sun, 
  Zap, 
  Battery, 
  ShoppingCart, 
  Sparkles, 
  Building,
  Euro,
  ArrowRight,
  ArrowDown,
  Users,
  TrendingUp,
  Lightbulb,
  Home,
  Factory
} from 'lucide-react';

interface RevenueStream {
  id: string;
  title: string;
  icon: React.ReactNode;
  description: string;
  flow: string[];
  amount: string;
  color: string;
  details: string[];
}

const revenueStreams: RevenueStream[] = [
  {
    id: 'solar',
    title: 'Zonnepaneel Participatie',
    icon: <Sun className="h-6 w-6" />,
    description: 'Inwoners & bedrijven investeren per m²',
    flow: ['Inwoners investeren €2,500/m²', 'Coöperatie beheert installatie', 'Verdeelde energiewinsten'],
    amount: '€8-12k/jaar',
    color: 'from-yellow-400 to-orange-500',
    details: [
      '25 kWp zonnecarport + bedrijfsdaken',
      '~80.000 kWh/jaar opbrengst',
      '15-20% rendement voor leden',
      'Lokale energiedeling via hub'
    ]
  },
  {
    id: 'charging',
    title: 'Publieke Snellaadstations',
    icon: <Zap className="h-6 w-6" />,
    description: 'Laadtarieven met ledenkorting',
    flow: ['Publiek betaalt €0.45/kWh', 'Leden krijgen 15% korting', 'Winst naar coöperatie'],
    amount: '€15-25k/jaar',
    color: 'from-blue-400 to-cyan-500',
    details: [
      '2× 60kW DC snelladers',
      '~150.000 kWh verkoop/jaar',
      '€0.10-0.15 marge per kWh',
      '24/7 publiek toegankelijk'
    ]
  },
  {
    id: 'battery',
    title: 'Batterij Arbitrage & Grid Services',
    icon: <Battery className="h-6 w-6" />,
    description: 'Goedkoop inkopen, duur verkopen',
    flow: ['Inkoop bij lage prijzen', 'Verkoop bij hoge prijzen', 'Grid stabilisatie services'],
    amount: '€5-8k/jaar',
    color: 'from-green-400 to-emerald-500',
    details: [
      '100 kWh batterijopslag',
      'Day-ahead markt arbitrage',
      'Frequentie regulatie diensten',
      'Piekvermogen reductie'
    ]
  },
  {
    id: 'business',
    title: 'Slimme Bedrijfsservices',
    icon: <ShoppingCart className="h-6 w-6" />,
    description: 'Koeling & logistiek optimalisatie',
    flow: ['Bedrijven betalen service fee', 'Smart energy management', 'Gedeelde besparingen'],
    amount: '€3-6k/jaar',
    color: 'from-purple-400 to-pink-500',
    details: [
      'Slim koelen supermarkten',
      'Logistiek laadoptimalisatie',
      'Energiecontract bundeling',
      'Monitoring & automatisering'
    ]
  },
  {
    id: 'events',
    title: 'Evenement Ondersteuning',
    icon: <Sparkles className="h-6 w-6" />,
    description: 'Groene stroom voor Pannerdense Kermis',
    flow: ['Batterijaverhuur aan events', 'CO₂-neutrale energielevering', 'Premium service fee'],
    amount: '€2-4k/jaar',
    color: 'from-pink-400 to-red-500',
    details: [
      'Pannerdense Kermis op batterijen',
      'Mobiele energiecontainers',
      'Event power management',
      'Groene stroom certificering'
    ]
  },
  {
    id: 'subsidies',
    title: 'Subsidies & Belastingvoordelen',
    icon: <Building className="h-6 w-6" />,
    description: 'Overheid & EU ondersteuning',
    flow: ['RVO SPRILA subsidie', 'Provincie Gelderland steun', 'EU Green Deal middelen'],
    amount: '€25-40k initial',
    color: 'from-indigo-400 to-blue-600',
    details: [
      'SPRILA subsidie tot 40%',
      'MIA/VAMIL afschrijving',
      'Provincie energietransitie',
      'EU Rural Development'
    ]
  },
  {
    id: 'social',
    title: 'Sociale Impact',
    icon: <Users className="h-6 w-6" />,
    description: 'Community fund & waardevermeerdering',
    flow: ['Coöperatiewinst naar dorpsfonds', 'Lokale banen & projecten', 'Vastgoedwaarde stijging'],
    amount: '€5-10k/jaar',
    color: 'from-emerald-400 to-teal-500',
    details: [
      '2-3 lokale banen gecreëerd',
      'Dorpsprojecten gefinancierd',
      'Vastgoedwaarde +3-5%',
      'Energie-onafhankelijkheid'
    ]
  }
];

const CentralHub = () => (
  <div className="relative">
    {/* Main Hub Circle */}
    <div className="w-32 h-32 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center shadow-2xl ring-4 ring-white/20">
      <div className="text-center text-white">
        <Lightbulb className="h-8 w-8 mx-auto mb-1" />
        <div className="text-xs font-bold">ENERGIE HUB</div>
        <div className="text-xs">Pannerden</div>
      </div>
    </div>
    
    {/* Flowing Energy Indicators */}
    <div className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-400 rounded-full animate-pulse"></div>
    <div className="absolute -bottom-2 -left-2 w-4 h-4 bg-blue-400 rounded-full animate-pulse delay-1000"></div>
    <div className="absolute top-1/2 -left-4 w-3 h-3 bg-green-400 rounded-full animate-pulse delay-500"></div>
  </div>
);

const FlowArrow = ({ direction = 'right', color = 'emerald' }: { direction?: 'right' | 'down' | 'up' | 'left', color?: string }) => {
  const ArrowIcon = direction === 'down' ? ArrowDown : ArrowRight;
  const rotation = direction === 'left' ? 'rotate-180' : direction === 'up' ? 'rotate-[-90deg]' : '';
  
  return (
    <div className={`flex items-center justify-center transform ${rotation}`}>
      <ArrowIcon className={`h-5 w-5 text-${color}-400 animate-pulse`} />
    </div>
  );
};

export const RevenueStreamInfographic = () => {
  const [selectedStream, setSelectedStream] = useState<string | null>(null);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
          Revenue Model: 7 Inkomstenbronnen
        </h3>
        <p className="text-slate-300 max-w-3xl mx-auto">
          Een hybride verdienmodel dat energietransitie, lokale economie en community impact combineert. 
          Klik op een revenue stream voor details.
        </p>
      </div>

      {/* Main Infographic - Desktop Layout */}
      <div className="hidden lg:block relative">
        {/* Central Hub */}
        <div className="flex justify-center mb-8">
          <CentralHub />
        </div>

        {/* Revenue Streams arranged in a circle around the hub */}
        <div className="relative grid grid-cols-3 gap-8 max-w-6xl mx-auto">
          {/* Top Row */}
          <div className="space-y-4">
            <RevenueStreamCard 
              stream={revenueStreams[0]} 
              isSelected={selectedStream === revenueStreams[0].id}
              onClick={() => setSelectedStream(selectedStream === revenueStreams[0].id ? null : revenueStreams[0].id)}
            />
            <div className="flex justify-center">
              <FlowArrow direction="down" color="yellow" />
            </div>
          </div>
          
          <div className="space-y-4">
            <RevenueStreamCard 
              stream={revenueStreams[1]} 
              isSelected={selectedStream === revenueStreams[1].id}
              onClick={() => setSelectedStream(selectedStream === revenueStreams[1].id ? null : revenueStreams[1].id)}
            />
            <div className="flex justify-center">
              <FlowArrow direction="down" color="blue" />
            </div>
          </div>

          <div className="space-y-4">
            <RevenueStreamCard 
              stream={revenueStreams[2]} 
              isSelected={selectedStream === revenueStreams[2].id}
              onClick={() => setSelectedStream(selectedStream === revenueStreams[2].id ? null : revenueStreams[2].id)}
            />
            <div className="flex justify-center">
              <FlowArrow direction="down" color="green" />
            </div>
          </div>

          {/* Middle Row - sides only */}
          <div className="flex items-center">
            <RevenueStreamCard 
              stream={revenueStreams[6]} 
              isSelected={selectedStream === revenueStreams[6].id}
              onClick={() => setSelectedStream(selectedStream === revenueStreams[6].id ? null : revenueStreams[6].id)}
            />
            <div className="ml-4">
              <FlowArrow direction="right" color="emerald" />
            </div>
          </div>
          
          <div></div> {/* Empty center space */}
          
          <div className="flex items-center justify-end">
            <div className="mr-4">
              <FlowArrow direction="left" color="purple" />
            </div>
            <RevenueStreamCard 
              stream={revenueStreams[3]} 
              isSelected={selectedStream === revenueStreams[3].id}
              onClick={() => setSelectedStream(selectedStream === revenueStreams[3].id ? null : revenueStreams[3].id)}
            />
          </div>

          {/* Bottom Row */}
          <div className="space-y-4">
            <div className="flex justify-center">
              <FlowArrow direction="up" color="indigo" />
            </div>
            <RevenueStreamCard 
              stream={revenueStreams[5]} 
              isSelected={selectedStream === revenueStreams[5].id}
              onClick={() => setSelectedStream(selectedStream === revenueStreams[5].id ? null : revenueStreams[5].id)}
            />
          </div>
          
          <div className="space-y-4">
            <div className="flex justify-center">
              <FlowArrow direction="up" color="pink" />
            </div>
            <RevenueStreamCard 
              stream={revenueStreams[4]} 
              isSelected={selectedStream === revenueStreams[4].id}
              onClick={() => setSelectedStream(selectedStream === revenueStreams[4].id ? null : revenueStreams[4].id)}
            />
          </div>

          <div></div> {/* Empty space */}
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="lg:hidden space-y-4">
        <div className="flex justify-center mb-6">
          <CentralHub />
        </div>
        
        <div className="grid gap-4 sm:grid-cols-2">
          {revenueStreams.map((stream) => (
            <RevenueStreamCard 
              key={stream.id}
              stream={stream} 
              isSelected={selectedStream === stream.id}
              onClick={() => setSelectedStream(selectedStream === stream.id ? null : stream.id)}
            />
          ))}
        </div>
      </div>

      {/* Financial Summary */}
      <div className="bg-slate-900/50 rounded-3xl p-6 ring-1 ring-white/10">
        <div className="grid md:grid-cols-3 gap-6">
          <div className="text-center">
            <TrendingUp className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
            <div className="text-2xl font-bold text-white">€60-105k</div>
            <div className="text-slate-300 text-sm">Totale jaaromzet (structureel)</div>
          </div>
          <div className="text-center">
            <Euro className="h-8 w-8 text-cyan-400 mx-auto mb-2" />
            <div className="text-2xl font-bold text-white">€25-40k</div>
            <div className="text-slate-300 text-sm">Initiële subsidies & steun</div>
          </div>
          <div className="text-center">
            <Users className="h-8 w-8 text-purple-400 mx-auto mb-2" />
            <div className="text-2xl font-bold text-white">12-15%</div>
            <div className="text-slate-300 text-sm">Verwacht rendement leden</div>
          </div>
        </div>
      </div>

      {/* Community Flow Diagram */}
      <div className="bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 rounded-3xl p-6 ring-1 ring-emerald-300/20">
        <h4 className="text-xl font-bold text-white mb-4 text-center">Geldstroom & Impact Flow</h4>
        
        <div className="flex flex-wrap justify-center items-center gap-4 text-center">
          <div className="flex items-center gap-2 bg-white/10 rounded-2xl px-4 py-2">
            <Home className="h-5 w-5 text-blue-400" />
            <span className="text-sm text-white">Bewoners</span>
          </div>
          
          <FlowArrow color="blue" />
          
          <div className="flex items-center gap-2 bg-white/10 rounded-2xl px-4 py-2">
            <Users className="h-5 w-5 text-emerald-400" />
            <span className="text-sm text-white">Coöperatie</span>
          </div>
          
          <FlowArrow color="emerald" />
          
          <div className="flex items-center gap-2 bg-white/10 rounded-2xl px-4 py-2">
            <Lightbulb className="h-5 w-5 text-yellow-400" />
            <span className="text-sm text-white">Energie Hub</span>
          </div>
          
          <FlowArrow color="yellow" />
          
          <div className="flex items-center gap-2 bg-white/10 rounded-2xl px-4 py-2">
            <Factory className="h-5 w-5 text-purple-400" />
            <span className="text-sm text-white">Bedrijven</span>
          </div>
          
          <FlowArrow color="purple" />
          
          <div className="flex items-center gap-2 bg-white/10 rounded-2xl px-4 py-2">
            <TrendingUp className="h-5 w-5 text-green-400" />
            <span className="text-sm text-white">Community Impact</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const RevenueStreamCard = ({ 
  stream, 
  isSelected, 
  onClick 
}: { 
  stream: RevenueStream; 
  isSelected: boolean; 
  onClick: () => void;
}) => {
  return (
    <div 
      onClick={onClick}
      className={`
        cursor-pointer transition-all duration-300 rounded-2xl p-4 ring-1 
        ${isSelected 
          ? 'bg-white/10 ring-white/30 scale-105 shadow-2xl' 
          : 'bg-slate-900/50 ring-white/10 hover:bg-white/5 hover:ring-white/20'
        }
      `}
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-xl bg-gradient-to-br ${stream.color}`}>
          {stream.icon}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-white text-sm leading-tight">{stream.title}</h4>
          <p className="text-xs text-slate-300 mt-1">{stream.description}</p>
          <div className="mt-2 flex items-center justify-between">
            <span className={`text-xs font-bold bg-gradient-to-r ${stream.color} bg-clip-text text-transparent`}>
              {stream.amount}
            </span>
            <span className="text-xs text-slate-400">
              {isSelected ? 'Minder details' : 'Meer details'}
            </span>
          </div>
        </div>
      </div>
      
      {isSelected && (
        <div className="mt-4 space-y-3 animate-in slide-in-from-top-2 duration-300">
          <div>
            <h5 className="text-xs font-semibold text-slate-200 mb-2">Geldstroom:</h5>
            <div className="space-y-1">
              {stream.flow.map((step, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-slate-300">
                  <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full"></div>
                  {step}
                </div>
              ))}
            </div>
          </div>
          
          <div>
            <h5 className="text-xs font-semibold text-slate-200 mb-2">Details:</h5>
            <div className="space-y-1">
              {stream.details.map((detail, i) => (
                <div key={i} className="text-xs text-slate-400">• {detail}</div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};